/**
 * Kanoniske R/A/~F-tekster ([D-131] trin 3: tooltips → registry) — FÆLLES
 * KILDE for vidensbankens serie-oneLiners (server/src/lib/revenue-model-doc.ts)
 * og [R | A | ~F]-togglens tooltips (web/src/composables/useRfMode.ts, vises
 * på Performance/Kampagner/Butiksbesøg/Telefon). Én kildelinje = doc-side og
 * UI-tooltips KAN ikke drifte.
 *
 * REGLER:
 *  - Filen SKAL forblive dependency-fri (ingen server-konstanter, ingen db) —
 *    den importeres af web-bundtet.
 *  - Filen er DOC-GATET (DOC_SYNC_SOURCES i server/src/lib/doc-sync.ts):
 *    indholdsændringer kræver `npm run doc:accept` i samme commit, og
 *    vidensbankens version/changelog bumpes KUN ved reelle ordlydsændringer
 *    (Charlies governance-regel — kilde-flytning/layout versioneres ikke).
 */

export type RfSeriesKey = 'r' | 'accept' | 'f';

/** Én-linjes definition pr. serie — ordret som vidensbanken viser dem. */
export const RF_SERIES_ONELINERS: Record<RfSeriesKey, string> = {
  r: 'R = realiseret: bogførings-dateret omsætning ("penge der ER bogført"), samme tidslinje som budgettet.',
  accept:
    'A = accept/ordreindgang (hovedserien, [D-128]+[D-130]): faktureret omsætning + forpligtet, endnu ufaktureret ordre-rest, dateret dér hvor kunden forpligtede sig.',
  f: '~F = fremskrevet forventet slutværdi: accept-dateret EV + åben ordre-rest + tilbuds-EV (× lukkerate). Mekanisk pipeline-EV med rater, ikke en prognose.',
};

/** Togglens A-tooltip bærer én ekstra sætning ud over oneLineren
 *  (sammenlignligheds-pointen — UI-kontekst, ikke doc-definition). */
export const RF_TOGGLE_ACCEPT_SUFFIX =
  ' Korte/lange og nye/gamle perioder er sammenlignelige.';

// ---------------------------------------------------------------------------
// Basis-tooltips fase 2 ([D-131] trin 3): de LANGE ærligheds-tooltips på
// Performance/Butiksbesøg/Telefon flyttet hertil som konstanter/parametriserede
// buildere, så teksterne har ÉN kilde og ikke kan drifte pr. side. Output er
// byte-identisk med de tidligere håndskrevne tekster i viewene (governance:
// ren kilde-flytning → doc:accept, INTET version-bump) — ENESTE afvigelse:
// "togglen i headeren" → "togglen i filterpanelet" (togglen flyttede ved
// panel-udrulningen 12-08, D-152; placerings-kontekst, ikke definition).
// NB: pageConventions.modes i revenue-model-doc.ts er dokumentations-
// formuleringer og må IKKE 1:1-genbruges her (adskilt med vilje).
// ---------------------------------------------------------------------------

/** da-DK procent: højst én decimal, decimalen kun når ≠ 0 (spejler
 *  Performances pctNumFmt). null/undefined → '—'. */
const pctFmt = new Intl.NumberFormat('da-DK', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});
function fmtPct(n: number | null | undefined): string {
  return n == null ? '—' : `${pctFmt.format(n)} %`;
}

/** Performances oms-KPI i A-visningen ([D-130]-ordreindgang). */
export const PERFORMANCE_ACCEPT_HELP =
  'A = ORDREINDGANG ([D-130], accept-dateret m. [D-128]-markøren): faktureret omsætning + forpligtet, endnu ufaktureret ordre-rest (kreditkunde-ordrer + accepterede ordrer uden bogført forudbetaling; forudbetalte ordrers cash tælles via forudbetalings-fakturaen, der bogføres straks). Backlog-testet: korte/lange og nye/gamle perioder er sammenlignelige (frisk-fejl ~0). HOVEDTAL = NETTO efter kreditnota (retur-dateret → let blandet tidslinje); brutto + rest-split vises med småt. Resten kan falde ved annullering (historisk ~9 % af forsvunden ordre-rest-kr). Web = Woo-ordredato (÷1,25), allerede ordreindgang. Forrige = periodens fulde måneder vs. lige så mange måneder umiddelbart før; YoY = samme måneder året før.';

/** Performances oms-KPI i R-visningen. */
export const PERFORMANCE_R_HELP =
  'R (§11.3) = varelinje-faktureret NETTO efter kreditnota ([D-114]-princippet), dateret på BOGFØRINGSDATO (udlevering), "penge der ER bogført". Matcher budgettets tidslinje; afviger fra accept-serien med konverterings-/leveringstiden. Web = BC-bogførte WEB-bilag (ikke Woo-ordredato).';

/** Performances rate-KpiCards ([D-088]/[D-119]-terminologien). */
export const PERFORMANCE_ROMS_HELP =
  'ROMS = omsætning ÷ annoncering (ad-spend) i perioden, [D-088]-terminologi. MER-niveau, IKKE attribueret kanal-ROAS. ROMS/MER beregnes kun over måneder m. spend-data, og YoY vises kun når sammenligningsmånederne også har spend-data (2024 = backfill:marketing-2024). Tælleren følger R/A/~F-togglen (A = ordreindgang, [D-130]).';
export const PERFORMANCE_MER_HELP =
  'MER = accept-omsætning ÷ (annoncering + retainere fra Retainere-tabellen) i perioden, [D-088]: hele marketing-omkostningen i nævneren. Retainer-celler kan indeholde ~estimater.';
export const PERFORMANCE_POAS_HELP =
  '~POAS = bruttofortjeneste ÷ ad-spend (§4.3). v2 ([D-119]): GP = netto-accept × MÅNEDENS value-entry-margin (ikke en flad sats), afspejler at marginen varierer pr. måned. Fortsat ~: VE er udleverings-dateret (ikke accept) og dækker ~75 % af salgs-entries.';
export const PERFORMANCE_DECOMP_HELP =
  'Rolling 12 mdr (fast, uafhængig af periodevalg, §4.4): baseline = forrige 12 mdr; growth = Δomsætning ÷ Δspend (beslutningstallet ved skalering op OG ned).';
export const PERFORMANCE_IDENTITY_HELP =
  'Identity-graph (§5.2): butik-accept-bilag m. ≥1 touchpoint FØR accept-datoen (Klaviyo-profil oprettet før, TDC-opkald før, webordre-historik før). Resten MODELLERES (§5.3). Strukturelt tal for hele vinduet 2025-01→ (følger ikke periodevalget).';
export const PERFORMANCE_ORGANIC_HELP =
  'Erstatningsværdi: organiske sessions × paid search-CPC i samme måned, brand-splittet. Omkostning = retainer-tabellens SEO + SEO-software (~ = estimat-celler). Omkostningslinse, ikke attribueret omsætning.';

/** Rate-felterne som ~F-tooltippen bruger (delmængde af /api/docs/revenue-
 *  models rates-objekt — feltnavnene er API'ets). */
export interface FWaterfallRates {
  tDays: number;
  rButikPct: number | null;
  rWebPct: number | null;
  returnsWindowFrom: string;
  returnsWindowTo: string;
  closingRateKrPct: number | null;
  closingWinFrom: string;
  closingWinTo: string;
  closingNWon: number;
  closingNDen: number;
  closingDecidedShareOfKrPct: number | null;
  g: number;
  k: number;
}

/** Performances ~F-tooltip m. ALLE rater, vinduer og n (§11.3-kravet). */
export function buildFWaterfallHelp(guardChosen: string, r: FWaterfallRates): string {
  const p = fmtPct;
  return (
    `~F (§11.3) = stadie-eksklusivt vandfald, hver krone tælles én gang, på sit mest fremskredne stadie: ` +
    `ben 1 faktureret-EV (guard '${guardChosen}': (accept-brutto − kreditnotaer) × (1 − r)) ` +
    `+ ben 2 åben ordre-REST (modregnet bogført forudbetaling, forudbetalte ordrer dobbelttælles IKKE, [D-130]) × (1 − r) ` +
    `+ ben 3 åbne tilbud (yngre end T=${r.tDays} d) × lukkerate × k × (1 − r) + web × (1 − r_web). ` +
    `Rater: r butik ${p(r.rButikPct)} / r_web ${p(r.rWebPct)} (12 mdr ${r.returnsWindowFrom}→${r.returnsWindowTo}); ` +
    `lukkerate ${p(r.closingRateKrPct)} kr-vægtet (modnet kohorte [D-451]: tilbud fra ${r.closingWinFrom}→${r.closingWinTo} med fuld modningstid T, won inden T ${r.closingNWon}/${r.closingNDen}; ⚠ OP-BIAS: raten gælder alle tilbud, men ganges på dem der endnu ikke er vundet, og ramte i backtesten ca. 2-3 gange det realiserede); ` +
    `g=${r.g} og k=${r.k} er ⚠ UKALIBREREDE. Mekanisk pipeline-EV, IKKE en kausal prognose; fald i ~F ved tilbudsafgørelse er re-datering til accept-perioden, ikke tabt omsætning.`
  );
}

/** Butiksbesøgs "omsætning pr. besøg"-tooltip (basis følger R/A/~F-togglen).
 *  rates = null før API-svaret er landet (→ basis-teksten alene). */
export function buildStoreVisitValueHelp(
  mode: RfSeriesKey,
  rates: { rButikPct: number; closingRateKrPct: number | null; tDays: number } | null,
): string {
  const base =
    'Netto-butiksomsætning (Item-linjer, ekskl. moms, UDEN kreditnota-netting) ÷ besøg. Basis følger R/A/~F-togglen i filterpanelet: R = bogføringsdato; A = ordreindgang ([D-130]): accept-dateret faktureret ([D-128]-markøren) + forpligtet, endnu ufaktureret ordre-rest; ~F = A-EV + tilbuds-EV.';
  if (mode !== 'f' || !rates) return base;
  return `${base} ~F-rater: r butik ${rates.rButikPct.toFixed(1).replace('.', ',')} %, lukkerate ${rates.closingRateKrPct == null ? '—' : rates.closingRateKrPct.toFixed(1).replace('.', ',')} % (modnet kohorte, [D-451]), T=${rates.tDays} d; g/k ukalibreret. Mekanisk pipeline-EV, ikke en prognose.`;
}

/** Telefons "omsætningsværdi pr. opkald"-tooltip. lastMonthLabel = allerede
 *  formateret månedsnavn (web-sidens formatMonthShortDa) eller null. */
export function buildCallValueHelp(ratio: number, lastMonthLabel: string | null): string {
  return (
    `Total netto-omsætning (butik + web, efter kreditnota) ÷ opkaldsvolumen, måned for måned. ` +
    `Nævneren er den harmoniserede volumen-serie (som Opkaldsvolumen-grafen): faktisk CDR for fulde CDR-måneder, ellers ~estimat = kø-tal × ${ratio ? ratio.toFixed(2).replace('.', ',') : '—'}, ` +
    `robust mod delvise kø-måneder (manglende TDC-kørapporter). Seneste md m. volumen: ${lastMonthLabel ?? '—'}. ` +
    `Basis følger R/A/~F-togglen i filterpanelet: R = bogført (budgettets tidslinje); A = ordreindgang ([D-130]: faktureret accept-dateret + forpligtet, endnu ufaktureret ordre-rest); ` +
    `~F = vandfalds-EV (rater m. forbehold, mekanisk pipeline-EV, ikke en prognose). ` +
    `HUSNØGLETAL for intensitet, IKKE kausal attribution: omsætningen kan hverken kø- eller opkalds-splittes.`
  );
}
